import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';
import { resolveCurrencyLabel } from '../utils/posCurrencyUtils';

const CurrencyContext = createContext();

const APP_CURRENCY_KEY = 'appCurrency';

function parseStoredCurrency(raw) {
  if (raw == null || raw === '') return null;
  if (raw === 'MRO' || raw === 'MRU') return raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed === 'MRO' || parsed === 'MRU') return parsed;
  } catch {
    /* plain string */
  }
  return null;
}

function readSavedCurrency() {
  if (typeof window === 'undefined') return 'MRO';
  try {
    const engine = getStorageEngine();
    const fromEngine = parseStoredCurrency(engine.get(KV_COLLECTION, APP_CURRENCY_KEY));
    if (fromEngine) return fromEngine;
    const fromLs = parseStoredCurrency(window.localStorage?.getItem(APP_CURRENCY_KEY));
    if (fromLs) return fromLs;
  } catch {
    /* ignore */
  }
  return 'MRO';
}

export const CurrencyProvider = ({ children }) => {
  const [currencyType, setCurrencyType] = useState(() => readSavedCurrency());
  const [conversionRate] = useState(10); // MRO = MRU × 10
  const skipNextPersistRef = useRef(true);

  useEffect(() => {
    const saved = readSavedCurrency();
    if (saved !== currencyType) {
      skipNextPersistRef.current = true;
      setCurrencyType(saved);
    }
  }, []);

  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    try {
      const engine = getStorageEngine();
      engine.put(KV_COLLECTION, APP_CURRENCY_KEY, currencyType);
      window.localStorage?.setItem(APP_CURRENCY_KEY, currencyType);
    } catch {
      /* ignore */
    }
  }, [currencyType]);

  const switchCurrency = useCallback((type) => {
    if (type === 'MRO' || type === 'MRU') {
      setCurrencyType(type);
    }
  }, []);

  const convertAmount = useCallback(
    (amount, fromCurrency = 'MRO', toCurrency = null) => {
      const targetCurrency = toCurrency || currencyType;

      if (fromCurrency === targetCurrency) {
        return amount;
      }

      if (fromCurrency === 'MRO' && targetCurrency === 'MRU') {
        return amount / conversionRate;
      }

      if (fromCurrency === 'MRU' && targetCurrency === 'MRO') {
        return amount * conversionRate;
      }

      return amount;
    },
    [currencyType, conversionRate]
  );

  const getCurrencySymbol = useCallback(() => currencyType, [currencyType]);

  const getCurrencyName = useCallback(
    (language = 'fr') => resolveCurrencyLabel(currencyType, language, { form: 'name' }),
    [currencyType]
  );

  const getCurrencyShortName = useCallback(
    (language = 'fr') => resolveCurrencyLabel(currencyType, language, { form: 'short' }),
    [currencyType]
  );

  const value = useMemo(
    () => ({
      currencyType,
      conversionRate,
      switchCurrency,
      convertAmount,
      getCurrencySymbol,
      getCurrencyName,
      getCurrencyShortName
    }),
    [
      currencyType,
      conversionRate,
      switchCurrency,
      convertAmount,
      getCurrencySymbol,
      getCurrencyName,
      getCurrencyShortName
    ]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
